import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { useUserAuth } from "../../context/UserAuthContext";
import { LogIn, LogOut, UserCircle } from "lucide-react";

export default function Navbar() {
  const { t, lang, setLang, languages } = useLang();
  const { user, loading: authLoading, googleLogin, logout } = useUserAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();
  const nav = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!langOpen) return;
    const onDoc = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [langOpen]);

  const currentLang = languages.find((l) => l.code === lang) || languages[0];

  const links = [
    { id: "property", label: t.nav.property },
    { id: "gallery", label: t.nav.gallery },
    { id: "amenities", label: t.nav.amenities },
    { id: "location", label: t.nav.location },
    { id: "nature", label: t.nav.nature || (lang === "de" ? "Natur" : "Nature") },
    { id: "reviews", label: t.nav.reviews },
  ];

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (!onHome) { nav("/#" + id); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const solid = scrolled || !onHome;
  const bg = solid ? "bg-[hsl(var(--background))]/95 backdrop-blur-md border-b border-[hsl(var(--border))]" : "bg-transparent";
  const txt = solid ? "text-[hsl(var(--foreground))]" : "text-white";

  return (
    <header data-testid="site-nav" className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${bg}`}>
      <div className="container-x flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="logo-link" className={`font-serif text-2xl md:text-[26px] tracking-tight ${txt}`} style={!solid ? { textShadow: "0 1px 14px rgba(0,0,0,0.45)" } : undefined}>
          Chalet <span className="italic text-[hsl(var(--accent))]">Steiermark</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-${l.id}`}
              className={`text-[12px] tracking-[0.18em] uppercase font-medium hover:text-[hsl(var(--accent))] transition-colors ${txt}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div ref={langRef} className="relative">
            <button
              data-testid="lang-toggle"
              onClick={() => setLangOpen((v) => !v)}
              aria-expanded={langOpen}
              className={`inline-flex items-center gap-1.5 text-[12px] tracking-[0.18em] uppercase font-medium ${txt} hover:text-[hsl(var(--accent))] transition-colors`}
            >
              <span className="text-base leading-none">{currentLang.flag}</span>
              <span>{currentLang.short}</span>
              <span aria-hidden className={`text-[9px] transition-transform ${langOpen ? "rotate-180" : ""}`}>▾</span>
            </button>
            {langOpen && (
              <div
                data-testid="lang-menu"
                className="absolute right-0 top-full mt-3 min-w-[220px] bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-2xl py-2 max-h-[80vh] overflow-y-auto"
                role="menu"
              >
                {languages.map((l) => (
                  <button
                    key={l.code}
                    role="menuitem"
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    data-testid={`lang-${l.code}`}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-[hsl(var(--muted))] transition-colors ${lang === l.code ? "text-[hsl(var(--accent))] bg-[hsl(var(--muted))]" : "text-[hsl(var(--foreground))]"}`}
                  >
                    <span className="text-lg leading-none">{l.flag}</span>
                    <span className="flex-1">{l.name}</span>
                    {lang === l.code && <span className="text-xs">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link to="/book" data-testid="nav-book-cta" className="btn-primary !py-2.5 !px-5 hidden md:inline-flex">
            {t.nav.book}
          </Link>
          {!authLoading && (
            user ? (
              <div className="hidden md:flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-[hsl(var(--border))]" />
                ) : (
                  <UserCircle size={24} className={txt} />
                )}
                <button
                  data-testid="user-logout-btn"
                  onClick={logout}
                  className={`text-xs tracking-wider uppercase font-medium hover:text-[hsl(var(--accent))] transition-colors ${txt}`}
                  title="Abmelden"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <button
                data-testid="google-login-btn"
                onClick={googleLogin}
                className={`hidden md:inline-flex items-center gap-1.5 text-[12px] tracking-[0.14em] uppercase font-medium hover:text-[hsl(var(--accent))] transition-colors ${txt}`}
              >
                <LogIn size={15} />
                <span>Login</span>
              </button>
            )
          )}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((p) => !p)}
            className={`lg:hidden ${txt}`}
            aria-label="Menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              {mobileOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <><path d="M3 6h18" /><path d="M3 12h18" /><path d="M3 18h18" /></>}
            </svg>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[hsl(var(--background))] border-t border-[hsl(var(--border))]">
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-left text-sm tracking-wide uppercase font-medium text-[hsl(var(--foreground))]">{l.label}</button>
            ))}
            <Link to="/book" onClick={() => setMobileOpen(false)} className="btn-primary mt-2 justify-center">{t.nav.book}</Link>
            {!authLoading && (
              user ? (
                <button onClick={() => { logout(); setMobileOpen(false); }} className="flex items-center gap-2 text-sm text-[hsl(var(--foreground))] mt-2">
                  <LogOut size={16} /> Abmelden ({user.name || user.email})
                </button>
              ) : (
                <button onClick={() => { googleLogin(); setMobileOpen(false); }} className="flex items-center gap-2 text-sm text-[hsl(var(--foreground))] mt-2">
                  <LogIn size={16} /> Mit Google anmelden
                </button>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
}
